import { useState, useCallback } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { StepUpload } from "./step-upload"
import { StepMapping } from "./step-mapping"
import { StepConfirm } from "./step-confirm"
import { StepProgress } from "./step-progress"
import {
  useUploadCsv,
  useExecuteImport,
  useDataImport,
  type DataImport,
  type ImportField,
} from "@/hooks/use-data-imports"
import { toast } from "sonner"

interface ImportWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultEntityType?: "companies" | "contacts"
}

type WizardStep = 1 | 2 | 3 | 4

const STEP_TITLES: Record<WizardStep, string> = {
  1: "Przeslij plik",
  2: "Mapowanie pol",
  3: "Potwierdzenie",
  4: "Import w toku",
}

const STEP_DESCRIPTIONS: Record<WizardStep, string> = {
  1: "Wybierz plik CSV i typ danych do importu",
  2: "Przypisz kolumny CSV do pol w systemie",
  3: "Sprawdz podsumowanie i rozpocznij import",
  4: "Sledz postep importu danych",
}

export function ImportWizard({ open, onOpenChange, defaultEntityType }: ImportWizardProps) {
  const [step, setStep] = useState<WizardStep>(1)
  const [importData, setImportData] = useState<DataImport | null>(null)
  const [csvHeaders, setCsvHeaders] = useState<string[]>([])
  const [csvPreview, setCsvPreview] = useState<string[][]>([])
  const [availableFields, setAvailableFields] = useState<ImportField[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({})

  const uploadMutation = useUploadCsv()
  const executeMutation = useExecuteImport()

  // Poll for updates during processing
  const { data: polledImport } = useDataImport(
    step === 4 ? importData?.id : undefined
  )

  const currentImport = step === 4 && polledImport?.data ? polledImport.data : importData

  const handleClose = useCallback(() => {
    // Prevent closing during processing
    if (step === 4 && currentImport?.status === "processing") {
      return
    }
    setStep(1)
    setImportData(null)
    setCsvHeaders([])
    setCsvPreview([])
    setAvailableFields([])
    setTotalRows(0)
    setFieldMapping({})
    onOpenChange(false)
  }, [step, currentImport, onOpenChange])

  const handleUpload = useCallback(
    async (file: File, entityType: string) => {
      try {
        const result = await uploadMutation.mutateAsync({ file, entityType })
        setImportData(result.data)
        setCsvHeaders(result.headers)
        setCsvPreview(result.preview)
        setAvailableFields(result.available_fields)
        setTotalRows(result.total_rows)

        // Auto-map fields by matching header names to field labels/keys
        const autoMapping: Record<string, string> = {}
        result.headers.forEach((header) => {
          const normalizedHeader = header.toLowerCase().trim()
          const matchedField = result.available_fields.find(
            (f) =>
              f.key.toLowerCase() === normalizedHeader ||
              f.label.toLowerCase() === normalizedHeader
          )
          if (matchedField) {
            autoMapping[header] = matchedField.key
          }
        })
        setFieldMapping(autoMapping)

        setStep(2)
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Blad przesylania pliku"
        toast.error(message)
      }
    },
    [uploadMutation]
  )

  const handleMappingComplete = useCallback(
    (mapping: Record<string, string>) => {
      setFieldMapping(mapping)
      setStep(3)
    },
    []
  )

  const handleExecute = useCallback(async () => {
    if (!importData) return
    try {
      const result = await executeMutation.mutateAsync({
        importId: importData.id,
        fieldMapping,
      })
      setImportData(result.data)
      setStep(4)
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Blad wykonania importu"
      toast.error(message)
    }
  }, [importData, fieldMapping, executeMutation])

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{STEP_TITLES[step]}</DialogTitle>
          <DialogDescription>{STEP_DESCRIPTIONS[step]}</DialogDescription>
        </DialogHeader>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 mb-4">
          {([1, 2, 3, 4] as WizardStep[]).map((s) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium border-2 transition-colors ${
                  s === step
                    ? "border-primary bg-primary text-primary-foreground"
                    : s < step
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-muted text-muted-foreground"
                }`}
              >
                {s < step ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  s
                )}
              </div>
              {s < 4 && (
                <div
                  className={`flex-1 h-0.5 ${
                    s < step ? "bg-primary" : "bg-muted"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step content */}
        {step === 1 && (
          <StepUpload
            onUpload={handleUpload}
            isLoading={uploadMutation.isPending}
            defaultEntityType={defaultEntityType}
          />
        )}

        {step === 2 && (
          <StepMapping
            csvHeaders={csvHeaders}
            csvPreview={csvPreview}
            availableFields={availableFields}
            fieldMapping={fieldMapping}
            onMappingChange={setFieldMapping}
            onNext={handleMappingComplete}
            onBack={() => setStep(1)}
            entityType={importData?.entity_type || "companies"}
          />
        )}

        {step === 3 && (
          <StepConfirm
            importData={importData}
            fieldMapping={fieldMapping}
            availableFields={availableFields}
            totalRows={totalRows}
            onExecute={handleExecute}
            onBack={() => setStep(2)}
            isLoading={executeMutation.isPending}
          />
        )}

        {step === 4 && (
          <StepProgress
            importData={currentImport}
            onClose={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
